import { VideoCallDiagnosticsData } from '../../../../packages/types/types';
import { ApplicationInsightsEvent } from '@/app-insights/application-insights-events';
import { reportError } from '@/utils/errorReporter';

type TrackEventFunction = (event: ApplicationInsightsEvent, properties?: Record<string, string>) => void;

// Helper to create base data for all logs
const createBaseData = (): Pick<VideoCallDiagnosticsData, 'timestamp' | 'platform'> => ({
  timestamp: new Date().toISOString(),
  platform: 'web',
});

// Helper to convert data to string record for Application Insights
const convertToStringRecord = (data: Partial<VideoCallDiagnosticsData>): Record<string, string> => {
  const stringRecord: Record<string, string> = {};
  
  try {
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (typeof value === 'object') {
            stringRecord[key] = JSON.stringify(value);
        } else {
          stringRecord[key] = String(value);
        }
      }
    });
  } catch (error) {
    // Return empty object if conversion fails
    reportError(error);
  }
  
  return stringRecord;
};

// Main logging functions
export const logVideoCallStart = (trackEvent: TrackEventFunction, data: Partial<VideoCallDiagnosticsData>) => {
  try {
    const diagnosticsData: Partial<VideoCallDiagnosticsData> = {
      ...createBaseData(),
      event_type: 'call_start',
      ...data,
    };

    trackEvent('MyIPractice_Web_Video_Call_Diagnostics', convertToStringRecord(diagnosticsData));
  } catch (error) {
    // Silently fail to prevent app crashes
    reportError(error);
  }
};

export const logVideoCallStateJoined = (trackEvent: TrackEventFunction, data: Partial<VideoCallDiagnosticsData>) => {
  try {
    const diagnosticsData: Partial<VideoCallDiagnosticsData> = {
      ...createBaseData(),
      event_type: 'call_state_joined',
      ...data,
    };

    trackEvent('MyIPractice_Web_Video_Call_Diagnostics', convertToStringRecord(diagnosticsData));
  } catch (error) {
    // Silently fail to prevent app crashes
    reportError(error);
  }
};

export const logVideoCallParticipantStateChange = (trackEvent: TrackEventFunction, data: Partial<VideoCallDiagnosticsData>) => {
  try {
    const diagnosticsData: Partial<VideoCallDiagnosticsData> = {
      ...createBaseData(),
      event_type: 'participant_state_change',
      ...data,
    };

    trackEvent('MyIPractice_Web_Video_Call_Diagnostics', convertToStringRecord(diagnosticsData));
  } catch (error) {
    // Silently fail to prevent app crashes
    reportError(error);
  }
};

export const logVideoCallError = (trackEvent: TrackEventFunction, eventType: VideoCallDiagnosticsData['event_type'], error: string) => {
  try {
    const diagnosticsData: Partial<VideoCallDiagnosticsData> = {
      ...createBaseData(),
      event_type: eventType,
      error_message: error,
    };

    trackEvent('MyIPractice_Web_Video_Call_Diagnostics_Error', convertToStringRecord(diagnosticsData));
  } catch (loggingError) {
    // Silently fail to prevent app crashes
    reportError(loggingError);
  }
};

// Helper to collect device information
export const collectWebDeviceInformation = async (trackEvent: TrackEventFunction): Promise<Partial<VideoCallDiagnosticsData>> => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const cameras = devices.filter(device => device.kind === 'videoinput');
    const microphones = devices.filter(device => device.kind === 'audioinput');
    const speakers = devices.filter(device => device.kind === 'audiooutput');

    // Check permissions - simplified approach
    let camera_permission: VideoCallDiagnosticsData['camera_permission'] = 'unknown';
    let microphone_permission: VideoCallDiagnosticsData['microphone_permission'] = 'unknown';
    let camera_browser_permission = false;
    let microphone_browser_permission = false;
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      camera_permission = 'granted';
      microphone_permission = 'granted';
      camera_browser_permission = true;
      microphone_browser_permission = true;
      stream.getTracks().forEach(track => track.stop());
    } catch (error) {
      if (error instanceof DOMException) {
        camera_permission = error.name === 'NotAllowedError' ? 'denied' : 'no_device';
        microphone_permission = error.name === 'NotAllowedError' ? 'denied' : 'no_device';
      }
    }

    return {
      camera_count: cameras.length,
      microphone_count: microphones.length,
      speaker_count: speakers.length,
      available_cameras: cameras.map(camera => ({ deviceId: camera.deviceId, label: camera.label })),
      available_microphones: microphones.map(microphone => ({ deviceId: microphone.deviceId, label: microphone.label })),
      camera_permission,
      microphone_permission,
      camera_browser_permission,
      microphone_browser_permission,
      user_agent: navigator.userAgent,
    };
  } catch (error) {
    logVideoCallError(
      trackEvent,
      'diagnostics_collection_failed',
      error instanceof Error ? error.message : 'Unknown error'
    );
    reportError(error);
    return {};
  }
}; 